import React from 'react';
import { useStatistics } from '../queries';
import { AreaChart } from '@tremor/react';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import TabLink from '@dashboard/components/TabLink';
import { formatTimestamp, formatValue } from '../dates';
import { useStatisticsContext } from '../Dashboard';
import { isNumber } from 'lodash';
import ChartMeta from './ChartMeta';
import Spinner from '@dashboard/components/Spinner';

function sumArray(array = []) {
  return array.reduce((accumulator, item) => {
    return accumulator + (isNumber(item.value) ? item.value : 0);
  }, 0);
}

const numberFormatter = value => Intl.NumberFormat('us').format(value).toString();

export default function NewSales() {
  const { unit, range } = useStatisticsContext();
  const { data: paymentsData, isLoading: isLoadingPayments } = useStatistics('numberOfPayments');
  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } =
    useStatistics('numberOfSubscriptions');
  const { data: submissionsData, isLoading: isLoadingSubmissions } =
    useStatistics('numberOfSubmissions');

  const chartPaymentsData = paymentsData?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    Payments: formatValue(timestamp, value),
  }));

  const chartSubscriptionsData = subscriptionsData?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    Subscriptions: formatValue(timestamp, value),
  }));

  const chartSubmissionsData = submissionsData?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    Submissions: formatValue(timestamp, value),
  }));

  const maxNumberOfPayments =
    paymentsData && Math.max(...paymentsData?.map(({ value }) => value), 0);
  const minNumberOfPayments =
    paymentsData && Math.min(...paymentsData?.map(({ value }) => value), 0);

  const maxNumberOfSubscriptions =
    subscriptionsData && Math.max(...subscriptionsData?.map(({ value }) => value), 0);
  const minNumberOfSubscriptions =
    subscriptionsData && Math.min(...subscriptionsData?.map(({ value }) => value), 0);

  const maxNumberOfSubmissions =
    (submissionsData && Math.max(...submissionsData?.map(({ value }) => value), 0)) || 0;
  const minNumberOfSubmissions =
    (submissionsData && Math.min(...submissionsData?.map(({ value }) => value), 0)) || 0;

  return (
    <Card variant="none">
      <TabGroup>
        <CardHeader variant="card">
          <CardHeaderTitle variant="card" className="w-full">
            <TabList className="flex gap-x-8 w-full">
              <Tab className="outline-none">
                {({ selected }) => (
                  <TabLink selected={selected}>
                    <CardHeaderTitle variant="card">
                      <div className="text-left">
                        <div className="text-base font-normal">Payments</div>

                        <div className="text-xl mt-1.5 font-normal">
                          {numberFormatter(sumArray(paymentsData))}
                        </div>
                      </div>
                    </CardHeaderTitle>
                  </TabLink>
                )}
              </Tab>

              <Tab className="outline-none">
                {({ selected }) => (
                  <TabLink selected={selected}>
                    <CardHeaderTitle variant="card">
                      <div className="text-left">
                        <div className="text-base font-normal">Subscriptions</div>

                        <div className="text-xl mt-1.5 font-normal">
                          {numberFormatter(sumArray(subscriptionsData))}
                        </div>
                      </div>
                    </CardHeaderTitle>
                  </TabLink>
                )}
              </Tab>

              <Tab className="outline-none">
                {({ selected }) => (
                  <TabLink selected={selected}>
                    <CardHeaderTitle variant="card">
                      <div className="text-left">
                        <div className="text-base font-normal">Submissions</div>

                        <div className="text-xl mt-1.5 font-normal">
                          {numberFormatter(sumArray(submissionsData))}
                        </div>
                      </div>
                    </CardHeaderTitle>
                  </TabLink>
                )}
              </Tab>
            </TabList>
          </CardHeaderTitle>
        </CardHeader>

        <CardContent variant="root">
          <TabPanels>
            <TabPanel>
              {isLoadingPayments ? (
                <div className="flex items-center justify-center h-44">
                  <Spinner />
                </div>
              ) : (
                <div className="relative pl-8">
                  {chartPaymentsData && (
                    <ChartMeta maxValue={maxNumberOfPayments} minValue={minNumberOfPayments} />
                  )}

                  <AreaChart
                    className="mt-4 h-44"
                    data={chartPaymentsData}
                    categories={['Payments']}
                    index="timestamp"
                    colors={['amber']}
                    showAnimation
                    allowDecimals={false}
                    showLegend={false}
                    showGridLines={false}
                    showYAxis={false}
                    showXAxis
                    startEndOnly
                    showGradient
                    padding={{ left: 16, right: 16 }}
                  />
                </div>
              )}
            </TabPanel>

            <TabPanel>
              {isLoadingSubscriptions ? (
                <div className="flex items-center justify-center h-44">
                  <Spinner />
                </div>
              ) : (
                <div className="relative pl-8">
                  {chartSubscriptionsData && (
                    <ChartMeta
                      maxValue={maxNumberOfSubscriptions}
                      minValue={minNumberOfSubscriptions}
                    />
                  )}

                  <AreaChart
                    className="mt-4 h-44"
                    data={chartSubscriptionsData}
                    categories={['Subscriptions']}
                    index="timestamp"
                    colors={['amber']}
                    showAnimation
                    allowDecimals={false}
                    showLegend={false}
                    showGridLines={false}
                    showYAxis={false}
                    showXAxis
                    startEndOnly
                    showGradient
                    padding={{ left: 16, right: 16 }}
                  />
                </div>
              )}
            </TabPanel>

            <TabPanel>
              {isLoadingSubmissions ? (
                <div className="flex items-center justify-center h-44">
                  <Spinner />
                </div>
              ) : (
                <div className="relative pl-8">
                  {chartSubmissionsData && (
                    <ChartMeta
                      maxValue={maxNumberOfSubmissions}
                      minValue={minNumberOfSubmissions}
                    />
                  )}

                  <AreaChart
                    className="mt-4 h-44"
                    data={chartSubmissionsData}
                    categories={['Submissions']}
                    index="timestamp"
                    colors={['amber']}
                    showAnimation
                    allowDecimals={false}
                    showLegend={false}
                    showGridLines={false}
                    showYAxis={false}
                    showXAxis
                    startEndOnly
                    showGradient
                    padding={{ left: 16, right: 16 }}
                  />
                </div>
              )}
            </TabPanel>
          </TabPanels>
        </CardContent>
      </TabGroup>
    </Card>
  );
}
